import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Camera, CameraOff, Keyboard, CheckCircle2, XCircle, AlertTriangle, RotateCcw, Users, Ticket, Share2, Copy, Radio } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function QRScanner() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedEvent = searchParams.get('event');

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(preselectedEvent || '');
  const [mode, setMode] = useState('camera');
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publicScanUrl, setPublicScanUrl] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);

  const scannerInstanceRef = useRef(null);
  const sseRef = useRef(null);

  // Fetch organizer events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/events/organizer/my-events`);
        const active = (res.data || []).filter(e => {
          if (e.is_recurring) return true;
          if (!e.date) return true;
          return new Date(e.date) >= new Date();
        });
        setEvents(active);
        if (preselectedEvent && active.some(e => e.event_id === preselectedEvent)) {
          setSelectedEventId(preselectedEvent);
        }
      } catch {
        toast.error('Failed to load events');
      }
    };
    fetchEvents();
  }, [preselectedEvent]);

  // SSE + Polling: subscribe to real-time scan events for the selected event
  useEffect(() => {
    if (!selectedEventId || !token) return;

    let pollTimer = null;
    let lastPollTime = new Date().toISOString();
    const seenScanIds = new Set();

    // Close existing SSE connection
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
      setSseConnected(false);
    }

    // Track whether SSE is delivering data
    let sseDataReceived = false;

    // --- SSE (primary, works when proxy doesn't buffer) ---
    const sseUrl = `${API_URL}/api/tickets/scan-events/${selectedEventId}?token=${token}`;
    const es = new EventSource(sseUrl);
    sseRef.current = es;

    es.onopen = () => setSseConnected(true);

    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        sseDataReceived = true;
        const scanId = data.scan_id || `${data.ticket?.ticket_id}-${data.time}`;
        if (seenScanIds.has(scanId)) return; // dedupe
        seenScanIds.add(scanId);

        const entry = {
          valid: data.valid,
          message: data.message,
          reason: data.reason || '',
          attendee: data.attendee,
          ticket: data.ticket,
          event_title: data.event_title,
          time: data.time ? new Date(data.time).toLocaleTimeString() : new Date().toLocaleTimeString(),
          source: 'remote',
        };
        setScanHistory(prev => [entry, ...prev].slice(0, 100));
        if (data.valid) {
          toast.success(`Checked in: ${data.attendee?.name || 'Attendee'} (${data.ticket?.ticket_id})`);
        } else {
          toast.error(`Rejected: ${data.ticket?.ticket_id || 'Unknown'} — ${data.reason || data.message}`);
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      setSseConnected(false);
    };

    // --- Polling fallback (reliable through any proxy) ---
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/tickets/scan-log/${selectedEventId}?since=${lastPollTime}`);
        const { entries, server_time } = res.data;
        if (entries && entries.length > 0) {
          lastPollTime = server_time;
          const newEntries = entries.filter(e => {
            const id = e.scan_id || `${e.ticket?.ticket_id}-${e.time}`;
            if (seenScanIds.has(id)) return false;
            seenScanIds.add(id);
            return true;
          });
          if (newEntries.length > 0) {
            const formatted = newEntries.map(data => ({
              valid: data.valid,
              message: data.message,
              reason: data.reason || '',
              attendee: data.attendee,
              ticket: data.ticket,
              event_title: data.event_title,
              time: data.time ? new Date(data.time).toLocaleTimeString() : new Date().toLocaleTimeString(),
              source: 'poll',
            }));
            setScanHistory(prev => [...formatted, ...prev].slice(0, 100));
            // Only show toasts from polling if SSE isn't working
            if (!sseDataReceived) {
              formatted.forEach(e => {
                if (e.valid) toast.success(`Checked in: ${e.attendee?.name || 'Attendee'}`);
                else toast.error(`Rejected: ${e.ticket?.ticket_id || 'Unknown'}`);
              });
            }
          }
        }
      } catch { /* polling is best-effort */ }
    };

    pollTimer = setInterval(poll, 2000);

    return () => {
      es.close();
      sseRef.current = null;
      setSseConnected(false);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [selectedEventId, token]);

  const startCamera = useCallback(async () => {
    if (scannerInstanceRef.current) return;
    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerInstanceRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => { handleScan(decodedText); },
        () => {}
      );
    } catch {
      toast.error('Camera access denied or not available');
      setScanning(false);
      scannerInstanceRef.current = null;
    }
  }, [selectedEventId]);

  const stopCamera = useCallback(async () => {
    if (scannerInstanceRef.current) {
      try {
        await scannerInstanceRef.current.stop();
        scannerInstanceRef.current.clear();
      } catch { /* cleanup */ }
      scannerInstanceRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      if (scannerInstanceRef.current) {
        try { scannerInstanceRef.current.stop(); scannerInstanceRef.current.clear(); } catch { /* */ }
      }
    };
  }, []);

  const handleScan = async (qrString) => {
    if (loading) return;
    await stopCamera();
    setLoading(true);
    setScanResult(null);

    try {
      const payload = { qr_string: qrString };
      if (selectedEventId) payload.event_id = selectedEventId;

      const res = await axios.post(`${API_URL}/api/tickets/validate-qr`, payload);
      const data = res.data;

      const result = {
        valid: data.valid,
        message: data.message,
        reason: data.reason || '',
        attendee: data.attendee,
        ticket: data.ticket,
        event_title: data.event_title || '',
        qr: qrString,
        time: new Date().toLocaleTimeString(),
        source: 'local',
      };
      setScanResult(result);
      // SSE will also broadcast this, but local scans go to history immediately
      setScanHistory(prev => [result, ...prev].slice(0, 100));

      if (data.valid) toast.success(data.message || 'Ticket validated!');
      else toast.error(data.message || 'Invalid ticket');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to validate ticket';
      const result = {
        valid: false,
        message: msg,
        reason: msg,
        qr: qrString,
        time: new Date().toLocaleTimeString(),
        source: 'local',
      };
      setScanResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 100));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    handleScan(manualCode.trim());
    setManualCode('');
  };

  const handleScanAnother = () => {
    setScanResult(null);
    if (mode === 'camera') startCamera();
  };

  const selectedEvent = events.find(e => e.event_id === selectedEventId);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="qr-scanner-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="scanner-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Ticket Scanner</h1>
            <p className="text-sm text-muted-foreground">Scan QR codes to validate entry</p>
          </div>
          {selectedEventId && (
            <Badge variant={sseConnected ? 'default' : 'outline'} className={`gap-1 ${sseConnected ? 'bg-emerald-600 text-white' : ''}`} data-testid="sse-status">
              <Radio className="w-3 h-3" />
              {sseConnected ? 'Live' : 'Offline'}
            </Badge>
          )}
        </div>

        {/* Event Selector */}
        <Card className="mb-4">
          <CardContent className="pt-4 pb-4">
            <label className="text-sm font-medium mb-2 block">Select Event</label>
            <Select value={selectedEventId} onValueChange={(val) => { setSelectedEventId(val); setPublicScanUrl(null); setScanHistory([]); setScanResult(null); }} data-testid="scanner-event-select">
              <SelectTrigger data-testid="scanner-event-trigger">
                <SelectValue placeholder="Choose an event to scan tickets for" />
              </SelectTrigger>
              <SelectContent>
                {events.map(ev => (
                  <SelectItem key={ev.event_id} value={ev.event_id} data-testid={`scanner-event-option-${ev.event_id}`}>
                    {ev.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Public Scan Link */}
        {selectedEventId && (
          <Card className="mb-4">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">External Scanner Link</h3>
                  <p className="text-xs text-muted-foreground">Share with door staff — no login needed</p>
                </div>
                {publicScanUrl ? (
                  <Button size="sm" variant="outline" onClick={() => { try { navigator.clipboard.writeText(publicScanUrl); toast.success('Link copied!'); } catch { toast.info('Copy the link manually'); } }} data-testid="copy-scan-link">
                    <Copy className="w-4 h-4 mr-2" /> Copy Link
                  </Button>
                ) : (
                  <Button size="sm" disabled={generatingKey} onClick={async () => {
                    setGeneratingKey(true);
                    try {
                      const res = await axios.post(`${API_URL}/api/tickets/generate-scan-key`, { event_id: selectedEventId });
                      const url = `${window.location.origin}${res.data.scan_url}`;
                      setPublicScanUrl(url);
                      try { navigator.clipboard.writeText(url); toast.success('Scan link generated and copied!'); } catch { toast.success('Scan link generated!'); }
                    } catch { toast.error('Failed to generate scan link'); }
                    setGeneratingKey(false);
                  }} data-testid="generate-scan-link">
                    <Share2 className="w-4 h-4 mr-2" /> {generatingKey ? 'Generating...' : 'Generate Link'}
                  </Button>
                )}
              </div>
              {publicScanUrl && (
                <div className="mt-2 p-2 bg-muted rounded text-xs font-mono break-all" data-testid="scan-link-display">{publicScanUrl}</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        {selectedEventId && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Card>
              <CardContent className="flex items-center gap-3 py-3 px-4">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="scanned-count">{scanHistory.filter(s => s.valid).length}</p>
                  <p className="text-xs text-muted-foreground">Checked In</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-3 py-3 px-4">
                <div className="p-2 rounded-lg bg-rose-500/10">
                  <XCircle className="w-5 h-5 text-rose-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="rejected-count">{scanHistory.filter(s => !s.valid).length}</p>
                  <p className="text-xs text-muted-foreground">Rejected</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <Button variant={mode === 'camera' ? 'default' : 'outline'} className={mode === 'camera' ? 'flex-1 btn-primary' : 'flex-1'} onClick={() => { setMode('camera'); setScanResult(null); }} data-testid="mode-camera-btn">
            <Camera className="w-4 h-4 mr-2" /> Camera
          </Button>
          <Button variant={mode === 'manual' ? 'default' : 'outline'} className={mode === 'manual' ? 'flex-1 btn-primary' : 'flex-1'} onClick={() => { stopCamera(); setMode('manual'); setScanResult(null); }} data-testid="mode-manual-btn">
            <Keyboard className="w-4 h-4 mr-2" /> Manual Entry
          </Button>
        </div>

        {/* Scanner Area */}
        {!selectedEventId ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Ticket className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">Select an event above to start scanning tickets</p>
            </CardContent>
          </Card>
        ) : scanResult ? (
          <ScanResultCard result={scanResult} onScanAnother={handleScanAnother} />
        ) : mode === 'camera' ? (
          <Card>
            <CardContent className="py-4">
              <div id="qr-reader" className="w-full rounded-lg overflow-hidden mb-4" style={{ minHeight: scanning ? 300 : 0 }} data-testid="qr-camera-viewport" />
              {!scanning ? (
                <Button onClick={startCamera} className="btn-primary w-full" data-testid="start-camera-btn" disabled={loading}>
                  <Camera className="w-4 h-4 mr-2" /> Start Camera
                </Button>
              ) : (
                <Button variant="outline" onClick={stopCamera} className="w-full" data-testid="stop-camera-btn">
                  <CameraOff className="w-4 h-4 mr-2" /> Stop Camera
                </Button>
              )}
              {loading && (
                <div className="flex items-center justify-center mt-4 gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-sm text-muted-foreground">Validating...</span>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-6">
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Ticket ID or QR Code</label>
                  <Input value={manualCode} onChange={e => setManualCode(e.target.value)} placeholder="Enter ticket ID or paste QR code value..." className="font-mono" data-testid="manual-code-input" />
                  <p className="text-xs text-muted-foreground mt-1">Enter the ticket ID (e.g. tckt_abc123) or the full QR code string</p>
                </div>
                <Button type="submit" className="btn-primary w-full" disabled={!manualCode.trim() || loading} data-testid="manual-validate-btn">
                  {loading ? 'Validating...' : 'Validate Ticket'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Live Scan Log */}
        {selectedEventId && scanHistory.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Scan Log ({scanHistory.length})</CardTitle>
                {sseConnected && <Badge variant="outline" className="text-xs gap-1"><Radio className="w-3 h-3 text-emerald-500" /> Live</Badge>}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="scan-history-list">
                {scanHistory.map((entry, idx) => (
                  <div
                    key={`${entry.ticket?.ticket_id || ''}-${idx}`}
                    className={`flex items-start justify-between py-2.5 px-3 rounded-lg border ${
                      entry.valid ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'
                    }`}
                    data-testid={`scan-entry-${idx}`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      {entry.valid ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold ${entry.valid ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {entry.valid ? 'VALID' : 'INVALID'}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground truncate" data-testid={`scan-ticket-id-${idx}`}>
                            {entry.ticket?.ticket_id || 'Not Found'}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate" data-testid={`scan-name-${idx}`}>
                          {entry.attendee?.name || 'Unknown'}
                        </p>
                        {!entry.valid && entry.reason && (
                          <p className="text-xs text-rose-400 truncate">{entry.reason}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">{entry.time}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}


function ScanResultCard({ result, onScanAnother }) {
  return (
    <Card className={`border-2 ${result.valid ? 'border-emerald-500' : 'border-rose-500'}`} data-testid="scan-result-card">
      <CardContent className="py-8">
        <div className="flex flex-col items-center text-center">
          {result.valid ? (
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-rose-500/10 flex items-center justify-center mb-4">
              <XCircle className="w-10 h-10 text-rose-500" />
            </div>
          )}
          <h2 className={`text-xl font-bold mb-1 ${result.valid ? 'text-emerald-500' : 'text-rose-500'}`} data-testid="scan-result-status">
            {result.valid ? 'VALID' : 'INVALID'}
          </h2>
          <p className="text-muted-foreground mb-4" data-testid="scan-result-message">{result.message}</p>

          <div className="w-full space-y-3 mb-4">
            {/* Ticket Info */}
            <div className="w-full bg-muted/50 rounded-lg p-4 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Ticket Details</span>
              </div>
              <p className="text-sm" data-testid="scan-result-ticket-id">
                <span className="text-muted-foreground">Ticket ID:</span>{' '}
                <span className="font-mono">{result.ticket?.ticket_id || 'Not Found'}</span>
              </p>
              {result.attendee?.name && (
                <p className="text-sm" data-testid="scan-attendee-name">
                  <span className="text-muted-foreground">Name:</span> {result.attendee.name}
                </p>
              )}
              {result.event_title && (
                <p className="text-sm" data-testid="scan-event-name">
                  <span className="text-muted-foreground">Event:</span> {result.event_title}
                </p>
              )}
              {result.ticket?.ticket_type_name && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Type:</span> {result.ticket.ticket_type_name}
                </p>
              )}
              {!result.valid && result.reason && (
                <p className="text-sm text-rose-400 mt-1">
                  <span className="text-muted-foreground">Reason:</span> {result.reason}
                </p>
              )}
            </div>
          </div>

          <Button onClick={onScanAnother} className="btn-primary w-full" data-testid="scan-another-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Scan Another Ticket
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
